import { Button, Input, Modal } from "@arco-design/web-react";
import { useEffect, useState } from "react";

import { formatSysStaffOptionLabel } from "../service/sysDeptUserService";
import type { SysStaffItem } from "../types";
import "./SysDeptUserPage.css";

type SysExistingStaffSelectModalProps = {
  state: { deptId: number; deptName: string } | null;
  items: SysStaffItem[];
  errorMessage: string;
  onClose: () => void;
  onClearError: () => void;
  onAddStaffToDept: (items: SysStaffItem[], deptId: number) => Promise<void>;
};

/** 选择已有员工加入指定部门的弹窗。 */
export function SysExistingStaffSelectModal({
  state,
  items,
  errorMessage,
  onClose,
  onClearError,
  onAddStaffToDept,
}: SysExistingStaffSelectModalProps) {
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    if (!state) {
      return;
    }
    onClearError();
    setKeyword("");
    setSelectedStaffIds(items.filter((item) => item.deptIds.includes(state.deptId)).map((item) => item.id));
  }, [state, items, onClearError]);

  /** 提交员工归属部门移动。 */
  async function handleOk() {
    if (!state || selectedStaffIds.length === 0) {
      return;
    }
    const selectedItems = items.filter((item) => selectedStaffIds.includes(item.id));
    if (selectedItems.length === 0) {
      return;
    }
    await onAddStaffToDept(selectedItems, state.deptId);
    onClose();
  }

  const filteredItems = items.filter((item) => {
    const text = `${item.realname} ${item.nickname} ${item.username} ${item.phone}`.toLowerCase();
    return text.includes(keyword.trim().toLowerCase());
  });

  return (
    <Modal
      title={state ? `添加已有员工到${state.deptName}` : "添加已有员工"}
      visible={Boolean(state)}
      onCancel={onClose}
      onOk={handleOk}
      okText="添加到部门"
      cancelText="取消"
      className="sys-dept-user-modal"
      footer={null}
      unmountOnExit
    >
      {errorMessage ? <div className="sys-dept-user-error">{errorMessage}</div> : null}
      <Input
        allowClear
        className="sys-dept-user-staff-choice-search"
        placeholder="输入姓名、账号或手机号筛选"
        value={keyword}
        onChange={setKeyword}
      />
      {filteredItems.length === 0 ? (
        <div className="sys-dept-user-empty">暂无可选择员工</div>
      ) : (
        <div className="sys-dept-user-staff-choice-list">
          {filteredItems.map((item) => (
            <label className="sys-dept-user-staff-choice" key={item.id}>
              <input
                aria-label={formatSysStaffOptionLabel(item)}
                checked={selectedStaffIds.includes(item.id)}
                name="sys-existing-staff"
                type="checkbox"
                onChange={(event) =>
                  setSelectedStaffIds((current) =>
                    event.target.checked
                      ? Array.from(new Set([...current, item.id]))
                      : current.filter((id) => id !== item.id),
                  )
                }
              />
              <span>
                <strong>{item.realname || item.nickname || item.username}</strong>
                <span className="sys-dept-user-tree-meta">{item.username}</span>
              </span>
              <span className="sys-dept-user-tree-meta">{item.mainDeptName || "未设置主部门"}</span>
            </label>
          ))}
        </div>
      )}
      <div className="sys-dept-user-modal-actions sys-dept-user-sticky-actions">
        <span>已选择 {selectedStaffIds.length} 人</span>
        <Button disabled={selectedStaffIds.length === 0} type="primary" onClick={handleOk}>
          添加选中员工
        </Button>
      </div>
    </Modal>
  );
}
